import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Users, CalendarRange, CalendarDays, CalendarClock, ClipboardCheck,
  TrendingUp, Megaphone, ListChecks, Activity, BarChart3, Download,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie,
} from "recharts";
import DeferredChart from "@/pages/admin/DeferredChart";

/* Donut size — kept fixed (no ResponsiveContainer) so Recharts doesn't fire
 * the harmless width(-1)/height(-1) first-paint warning for circular charts. */
const DONUT_SIZE = 160;
import {
  TIME_WINDOWS, filterByWindow, nyDateStr,
  leadsBySource, leadsByCampaign, leadsByStatus, leadsByPriority,
  leadsByOnboarding, leadsByMonth, topNWithOther, onboardingStateOf,
  STATUS_LABELS, PRIORITY_LABELS, ONBOARDING_LABELS,
  STATUS_COLORS, PRIORITY_COLORS, ONBOARDING_COLORS,
  prettyMonth, prettyMonthShort,
} from "./reportHelpers";

/* ── Mini bar row used in Source / Campaign breakdown lists ─────────────── */
function BarRow({ label, count, max, total, onClick, testId, color = "var(--swipe-accent)" }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  const share = total > 0 ? Math.round((count / total) * 100) : 0;
  const Tag = onClick ? "button" : "div";
  const cls = onClick
    ? "w-full text-left group rounded-lg border border-slate-100 hover:border-[var(--swipe-accent)]/40 hover:bg-[var(--swipe-accent-soft)]/40 px-3 py-2.5 transition-colors"
    : "rounded-lg border border-slate-100 px-3 py-2.5";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} data-testid={testId} className={cls}>
      <div className="flex items-center justify-between gap-3">
        <span className="font-display font-bold text-[13.5px] text-[var(--swipe-navy)] truncate flex-1" title={label}>
          {label}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[11.5px] text-slate-500 tabular-nums">{share}%</span>
          <span className="text-[14px] font-extrabold text-[var(--swipe-navy)] tabular-nums">{count}</span>
        </div>
      </div>
      <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </Tag>
  );
}

/* ── Section card wrapper ───────────────────────────────────────────────── */
function ReportSection({ icon: Icon, title, subtitle, children, testId, action }) {
  return (
    <div
      className="rounded-2xl border border-[var(--swipe-accent)]/15 bg-white p-5"
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-[16px] font-extrabold text-[var(--swipe-navy)] leading-tight truncate">
              {title}
            </h3>
            {subtitle ? <div className="text-[11.5px] text-slate-500 truncate">{subtitle}</div> : null}
          </div>
        </div>
        {action || null}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="py-6 text-center text-[13px] text-slate-400 italic">
      {label || "No data for the selected period."}
    </div>
  );
}

/* ── Mini KPI ───────────────────────────────────────────────────────────── */
function Kpi({ label, value, icon: Icon, testId }) {
  return (
    <div
      className="rounded-xl border border-[var(--swipe-accent)]/15 bg-white px-4 py-3.5 flex items-start justify-between gap-3"
      data-testid={testId}
    >
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">{label}</div>
        <div className="mt-1.5 text-[1.65rem] font-extrabold text-[var(--swipe-navy)] leading-none tabular-nums">{value}</div>
      </div>
      <div className="w-9 h-9 rounded-lg bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" strokeWidth={2.25} />
      </div>
    </div>
  );
}

/* ── Tooltip + Pie label helpers ────────────────────────────────────────── */
function MonthlyTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-[var(--swipe-navy)] text-white px-3 py-2 rounded-lg shadow-lg text-[12px]">
      <div className="font-semibold">{prettyMonth(p.month)}</div>
      <div className="text-white/80">{p.count} lead{p.count === 1 ? "" : "s"}</div>
    </div>
  );
}

function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-[var(--swipe-navy)] text-white px-3 py-2 rounded-lg shadow-lg text-[12px]">
      <div className="font-semibold">{d.name}</div>
      <div className="text-white/80">{d.value} lead{d.value === 1 ? "" : "s"}</div>
    </div>
  );
}

/* ── CSV export ─────────────────────────────────────────────────────────── */
function escCsv(v) {
  if (v === null || v === undefined) return "";
  const s = String(v).replace(/\r?\n/g, " ").replace(/"/g, '""');
  return /[",]/.test(s) ? `"${s}"` : s;
}

function buildReportCsv({ windowLabel, total, bySource, byCampaign, byMonth, byStatus, byPriority, byOnboarding }) {
  const lines = [];
  const push = (...row) => lines.push(row.map(escCsv).join(","));
  push("Swipetronics — Lead Report");
  push("Window", windowLabel);
  push("Generated", new Date().toLocaleString("en-US"));
  push("Total leads", total);
  push("");

  const dim = (heading, rows, labelFn = (k) => k) => {
    push(heading);
    push("Key", "Count", "Share %");
    for (const r of rows) {
      const share = total > 0 ? Math.round((r.count / total) * 100) : 0;
      push(labelFn(r.key), r.count, `${share}%`);
    }
    push("");
  };

  dim("Leads by Source", bySource);
  dim("Leads by Campaign (utm_campaign)", byCampaign);
  dim("Leads by Month", byMonth.map((m) => ({ key: m.month, count: m.count })));
  dim("Leads by Status", byStatus, (k) => STATUS_LABELS[k] || k);
  dim("Leads by Priority", byPriority, (k) => PRIORITY_LABELS[k] || k);
  dim("Onboarding breakdown", byOnboarding, (k) => ONBOARDING_LABELS[k] || k);

  return lines.join("\r\n");
}

/* ── Reports tab ────────────────────────────────────────────────────────── */
export default function ReportsTab({ leads, onPickSource }) {
  const [windowKey, setWindowKey] = useState("all");
  const windowLabel = TIME_WINDOWS.find((w) => w.v === windowKey)?.label || "All time";

  // Scoped lead set — already excludes archived/trash/test upstream.
  const scoped = useMemo(() => filterByWindow(leads || [], windowKey), [leads, windowKey]);
  const total = scoped.length;

  // KPI summary (always relative to scoped window for consistency).
  const today = nyDateStr();
  const monthPrefix = today.slice(0, 7);
  const kpis = useMemo(() => {
    const t = today;
    const m = monthPrefix;
    const onb = scoped.filter((l) => onboardingStateOf(l) === "submitted").length;
    let todayCount = 0;
    let monthCount = 0;
    for (const l of scoped) {
      const d = (l.created_at ? new Date(l.created_at) : null);
      if (!d) continue;
      const dStr = nyDateStr(d);
      if (dStr === t) todayCount += 1;
      if (dStr.startsWith(m)) monthCount += 1;
    }
    return { todayCount, monthCount, submitted: onb };
  }, [scoped, today, monthPrefix]);

  // Aggregations.
  const bySource = useMemo(() => leadsBySource(scoped), [scoped]);
  const byCampaign = useMemo(() => leadsByCampaign(scoped), [scoped]);
  const byStatus = useMemo(() => leadsByStatus(scoped), [scoped]);
  const byPriority = useMemo(() => leadsByPriority(scoped), [scoped]);
  const byOnboarding = useMemo(() => leadsByOnboarding(scoped), [scoped]);
  const byMonth = useMemo(() => leadsByMonth(scoped, 12), [scoped]);

  const topSources = useMemo(() => topNWithOther(bySource, 10), [bySource]);
  const topCampaigns = useMemo(() => topNWithOther(byCampaign, 10), [byCampaign]);
  const maxSource = topSources[0]?.count || 1;
  const maxCampaign = topCampaigns[0]?.count || 1;

  // Pie data sets — show only nonzero buckets.
  const statusPie = useMemo(() => byStatus.map((r) => ({
    name: STATUS_LABELS[r.key] || r.key,
    value: r.count,
    color: STATUS_COLORS[r.key] || "#94A3B8",
  })), [byStatus]);
  const priorityPie = useMemo(() => byPriority.map((r) => ({
    name: PRIORITY_LABELS[r.key] || r.key,
    value: r.count,
    color: PRIORITY_COLORS[r.key] || "#94A3B8",
  })), [byPriority]);
  const onboardingPie = useMemo(() => byOnboarding.map((r) => ({
    name: ONBOARDING_LABELS[r.key] || r.key,
    value: r.count,
    color: ONBOARDING_COLORS[r.key] || "#94A3B8",
  })), [byOnboarding]);

  // Monthly chart data — render with short month label.
  const monthlyData = useMemo(
    () => byMonth.map((m) => ({ ...m, label: prettyMonthShort(m.month) })),
    [byMonth],
  );
  const monthlyTotal = useMemo(() => monthlyData.reduce((s, r) => s + r.count, 0), [monthlyData]);

  const exportCsv = () => {
    const csv = buildReportCsv({
      windowLabel, total, bySource, byCampaign, byMonth, byStatus, byPriority, byOnboarding,
    });
    const dateTag = new Date().toISOString().slice(0, 10);
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `swipetronics-report-${windowKey}-${dateTag}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Report exported");
  };

  return (
    <div data-testid="reports-tab" className="space-y-5">
      {/* Header — window toggle + CSV export */}
      <div
        className="rounded-xl border border-[var(--swipe-accent)]/15 bg-white px-5 py-3.5 flex items-center justify-between gap-3 flex-wrap"
        data-testid="reports-toolbar"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center shrink-0">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-[15px] font-extrabold text-[var(--swipe-navy)] leading-tight">Reports</div>
            <div className="text-[11.5px] text-slate-500 truncate">
              Read-only metrics · Active leads only (excludes test, archived &amp; trash) · {total} total
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center rounded-lg border border-slate-200 overflow-hidden" data-testid="reports-window">
            {TIME_WINDOWS.map((w) => (
              <button
                key={w.v}
                type="button"
                onClick={() => setWindowKey(w.v)}
                data-testid={`reports-window-${w.v}`}
                className={`px-3 py-1.5 text-[12.5px] font-semibold border-r last:border-r-0 border-slate-200 transition-colors ${
                  windowKey === w.v
                    ? "bg-[var(--swipe-accent)] text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={exportCsv}
            data-testid="reports-export-csv"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-[var(--swipe-accent)] hover:text-[var(--swipe-accent)] text-[12.5px] font-semibold text-slate-700 transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {/* KPI summary row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Total Leads" value={total} icon={Users} testId="reports-kpi-total" />
        <Kpi label="This Month" value={kpis.monthCount} icon={CalendarRange} testId="reports-kpi-month" />
        <Kpi label="Today" value={kpis.todayCount} icon={CalendarClock} testId="reports-kpi-today" />
        <Kpi label="Onboarding Submitted" value={kpis.submitted} icon={ClipboardCheck} testId="reports-kpi-submitted" />
      </div>

      {/* Monthly chart */}
      <ReportSection
        icon={CalendarDays}
        title="Leads by Month"
        subtitle={`Last 12 months · ${monthlyTotal} leads in window${windowKey === "all" ? "" : " (filtered)"}`}
        testId="reports-monthly"
      >
        {monthlyTotal === 0 ? (
          <EmptyState label="No leads in the last 12 months for the selected window." />
        ) : (
          <DeferredChart height={220} testId="reports-monthly-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200} initialDimension={{ width: 600, height: 200 }}>
              <BarChart data={monthlyData} margin={{ top: 8, right: 10, left: -18, bottom: 4 }} barCategoryGap="22%">
                <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 11, fontWeight: 600 }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(0, 114, 255, 0.05)" }} content={<MonthlyTooltip />} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {monthlyData.map((d) => (
                    <Cell key={d.month} fill="#0072FF" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </DeferredChart>
        )}
      </ReportSection>

      {/* Source + Campaign — side by side on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ReportSection
          icon={TrendingUp}
          title="Leads by Source"
          subtitle={`${bySource.length} unique source${bySource.length === 1 ? "" : "s"} · click to view`}
          testId="reports-by-source"
        >
          {topSources.length === 0 ? (
            <EmptyState label="No leads in the selected window." />
          ) : (
            <ul className="space-y-2.5">
              {topSources.map((r, idx) => (
                <li key={r.key}>
                  <BarRow
                    label={r.key}
                    count={r.count}
                    max={maxSource}
                    total={total}
                    onClick={r._isOther ? undefined : () => onPickSource?.(r.key)}
                    testId={`reports-source-row-${idx}`}
                  />
                </li>
              ))}
            </ul>
          )}
        </ReportSection>

        <ReportSection
          icon={Megaphone}
          title="Leads by Campaign"
          subtitle={`UTM campaign attribution · ${byCampaign.length} unique value${byCampaign.length === 1 ? "" : "s"}`}
          testId="reports-by-campaign"
        >
          {topCampaigns.length === 0 ? (
            <EmptyState label="No leads in the selected window." />
          ) : (
            <ul className="space-y-2.5">
              {topCampaigns.map((r, idx) => (
                <li key={r.key}>
                  <BarRow
                    label={r.key}
                    count={r.count}
                    max={maxCampaign}
                    total={total}
                    testId={`reports-campaign-row-${idx}`}
                    color="#6366F1"
                  />
                </li>
              ))}
            </ul>
          )}
        </ReportSection>
      </div>

      {/* Status / Priority / Onboarding — donut row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ReportSection icon={ListChecks} title="Status breakdown" testId="reports-by-status">
          <DonutBlock data={statusPie} total={total} />
        </ReportSection>

        <ReportSection icon={Activity} title="Priority breakdown" testId="reports-by-priority">
          <DonutBlock data={priorityPie} total={total} />
        </ReportSection>

        <ReportSection icon={ClipboardCheck} title="Onboarding breakdown" testId="reports-by-onboarding">
          <DonutBlock data={onboardingPie} total={total} />
        </ReportSection>
      </div>
    </div>
  );
}

/* Reusable donut + legend block. Renders an empty state when no data. */
function DonutBlock({ data, total }) {
  const usable = data.filter((d) => d.value > 0);
  if (total === 0 || usable.length === 0) {
    return <EmptyState label="No leads in the selected window." />;
  }
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div style={{ width: DONUT_SIZE, height: DONUT_SIZE }} className="shrink-0" data-testid="reports-donut-chart">
        <PieChart width={DONUT_SIZE} height={DONUT_SIZE}>
          <Tooltip content={<PieTooltip />} />
          <Pie
            data={usable}
            dataKey="value"
            nameKey="name"
            innerRadius={42}
            outerRadius={68}
            paddingAngle={2}
            stroke="none"
            isAnimationActive={false}
          >
            {usable.map((d) => <Cell key={d.name} fill={d.color} />)}
          </Pie>
        </PieChart>
      </div>
      <ul className="flex-1 min-w-[140px] space-y-1.5">
        {usable.map((d) => {
          const share = total > 0 ? Math.round((d.value / total) * 100) : 0;
          return (
            <li key={d.name} className="flex items-center justify-between gap-3 text-[12.5px]">
              <span className="flex items-center gap-2 min-w-0">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.color }} />
                <span className="font-semibold text-[var(--swipe-navy)] truncate">{d.name}</span>
              </span>
              <span className="text-slate-500 tabular-nums shrink-0">
                <span className="font-bold text-[var(--swipe-navy)]">{d.value}</span> · {share}%
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
